import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { api } from "@/lib/api";

const money = (n, mode) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${v} ${mode === "inc" ? "INC" : "EX"} GST`;
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 text-right">{value || "—"}</span>
  </div>
);

const Notice = ({ icon: Icon, tone, title, children }) => (
  <div data-testid="po-notice" className={`rounded-xl border p-5 text-center ${tone}`}>
    <Icon className="h-8 w-8 mx-auto mb-2" />
    <h2 className="text-lg font-semibold">{title}</h2>
    <p className="text-sm mt-1 opacity-90">{children}</p>
  </div>
);

export default function POInput() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [summary, setSummary] = useState(null);
  const [existingPo, setExistingPo] = useState("");
  const [po, setPo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fetchedFor = useRef(null);
  useEffect(() => {
    if (fetchedFor.current === token) return;
    fetchedFor.current = token;
    api.get(`/public/po/${token}`)
      .then((r) => { setState(r.data.state); setSummary(r.data.summary || null); setExistingPo(r.data.po_number || ""); })
      .catch(() => setState("not_found"));
  }, [token]);

  const submit = async () => {
    if (!po.trim()) { setError("Please enter a PO number."); return; }
    setSubmitting(true); setError("");
    try {
      const r = await api.post(`/public/po/${token}`, { po_number: po.trim() });
      setState(r.data.state);
      if (r.data.po_number) setExistingPo(r.data.po_number);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col" data-testid="po-page">
      <header className="bg-[#005596] text-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          <span className="font-semibold">Dowdens Group — Purchase Order</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-6 py-8">
        {state === "loading" && (
          <div className="flex items-center justify-center py-20 text-slate-500" data-testid="po-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {state === "not_found" && (
          <Notice icon={AlertTriangle} tone="bg-amber-50 border-amber-200 text-amber-800" title="Link not found">
            This PO link is invalid or has been replaced by a newer one. Please use the most recent PO Request email, or ask the Marketing team to resend it.
          </Notice>
        )}
        {state === "expired" && (
          <Notice icon={AlertTriangle} tone="bg-amber-50 border-amber-200 text-amber-800" title="Link expired">
            This PO link has expired (it is valid for 5 working days). Please ask the Marketing team to resend the PO Request.
          </Notice>
        )}
        {state === "used" && (
          <Notice icon={CheckCircle2} tone="bg-emerald-50 border-emerald-200 text-emerald-800" title="PO already submitted">
            A PO number has already been submitted using this link. No further action is needed.
          </Notice>
        )}
        {state === "already_recorded" && (
          <Notice icon={CheckCircle2} tone="bg-emerald-50 border-emerald-200 text-emerald-800" title="PO already recorded">
            A PO number{existingPo ? ` (${existingPo})` : ""} has already been recorded for this RFQ. No further action is needed.
          </Notice>
        )}
        {state === "submitted" && (
          <Notice icon={CheckCircle2} tone="bg-emerald-50 border-emerald-200 text-emerald-800" title="PO number submitted">
            Thank you — PO number <strong>{existingPo}</strong> has been recorded and the requester has been notified.
          </Notice>
        )}

        {(state === "ok" || state === "already_recorded") && summary && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="po-summary">
            <h1 className="text-base font-semibold text-slate-900 mb-2">Request summary</h1>
            <Row label="RFQ" value={summary.rfq_number} />
            {summary.request_number && <Row label="Request" value={summary.request_number} />}
            <Row label="Branch" value={summary.branch} />
            <Row label="Job Number" value={summary.job_number} />
            <Row label="Supplier" value={summary.supplier_name} />
            <Row label="Approved total" value={money(summary.total, summary.gst_mode)} />
          </div>
        )}

        {state === "ok" && (
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="po-form">
            <label htmlFor="po" className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
            <input id="po" data-testid="po-input" value={po}
              onChange={(e) => setPo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="Enter the PO number"
              className="w-full h-11 rounded-lg border border-slate-300 px-3 text-slate-900 focus:border-[#005596] focus:outline-none focus:ring-2 focus:ring-[#005596]/20" />
            {error && <p className="text-sm text-red-600 mt-2" data-testid="po-error">{error}</p>}
            <button onClick={submit} disabled={submitting} data-testid="po-submit"
              className="mt-4 w-full h-11 rounded-lg bg-[#005596] text-white font-semibold hover:bg-[#00477e] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit PO Number
            </button>
            <p className="text-xs text-slate-400 mt-3 text-center">This link is for entering the PO number and expires after 5 working days.</p>
          </div>
        )}
      </main>
    </div>
  );
}
